import { ReactComponent as SecurityShieldIcon } from "assets/images/icons/icon-shield-security.svg"
import { KeyHealthScope } from "types/keyHealth"

interface HealthCheckLoadingOverlayProps {
  scope: KeyHealthScope
}

const SCOPE_MESSAGES: Partial<
  Record<KeyHealthScope, { title: string; subtitle: string }>
> = {
  projectDetail: {
    title: "Verifying Project Access",
    subtitle: "Checking permissions before loading project content",
  },
  projectComponent: {
    title: "Verifying Component Access",
    subtitle: "Checking permissions before loading component content",
  },
  global: {
    title: "Verifying Conversation Access",
    subtitle: "Checking permissions before loading messages",
  },
  build: {
    title: "Verifying Build Access",
    subtitle: "Checking permissions before loading build content",
  },
  buildComponent: {
    title: "Verifying Build Component Access",
    subtitle: "Checking permissions before loading build component content",
  },
}

const HealthCheckLoadingOverlay = ({
  scope,
}: HealthCheckLoadingOverlayProps) => {
  const message = SCOPE_MESSAGES[scope] ?? {
    title: "Verifying Encryption Keys",
    subtitle: "Please wait a moment",
  }

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ background: "#F7F7F7", zIndex: 50 }}
    >
      {/* Center card */}
      <div
        className="flex flex-col items-center"
        style={{
          background: "#FFFFFF",
          borderRadius: 20,
          boxShadow:
            "0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 40px -8px rgba(26,71,240,0.12)",
          padding: "48px 56px",
          width: 400,
          gap: 28,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Animated icon */}
        <div className="relative" style={{ width: 112, height: 112 }}>
          {/* Outer pulse ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: 112,
              height: 112,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background:
                "linear-gradient(135deg, rgba(26,71,240,0.08) 0%, rgba(253,190,68,0.08) 100%)",
              animation: "hcPulse 2s ease-in-out infinite",
            }}
          />

          {/* Outer spinning ring — blue */}
          <div
            className="absolute rounded-full"
            style={{
              width: 96,
              height: 96,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "2.5px solid transparent",
              borderTopColor: "#1A47F0",
              borderRightColor: "rgba(26,71,240,0.25)",
              borderBottomColor: "rgba(26,71,240,0.1)",
              borderLeftColor: "rgba(26,71,240,0.4)",
              borderRadius: "50%",
              animation: "hcSpin 1s linear infinite",
            }}
          />

          {/* Inner spinning ring — yellow, reverse */}
          <div
            className="absolute rounded-full"
            style={{
              width: 76,
              height: 76,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "2.5px solid transparent",
              borderTopColor: "rgba(253,190,68,0.2)",
              borderRightColor: "#FDBE44",
              borderBottomColor: "rgba(253,190,68,0.4)",
              borderLeftColor: "rgba(253,190,68,0.1)",
              borderRadius: "50%",
              animation: "hcSpinReverse 1.4s linear infinite",
            }}
          />

          {/* Glow behind icon */}
          <div
            className="absolute rounded-full"
            style={{
              width: 58,
              height: 58,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background:
                "radial-gradient(circle, rgba(26,71,240,0.15) 0%, transparent 70%)",
              animation: "hcGlow 2s ease-in-out infinite",
            }}
          />

          {/* Shield icon */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              width: 52,
              height: 52,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              animation: "hcIconPulse 2s ease-in-out infinite",
            }}
          >
            <SecurityShieldIcon
              style={{
                width: 52,
                height: 52,
                color: "#1A47F0",
                filter: "drop-shadow(0 4px 10px rgba(26,71,240,0.25))",
              }}
            />
          </div>
        </div>

        {/* Text block */}
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: 6 }}
        >
          <p
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.01em",
              lineHeight: "26px",
              animation: "hcFadeInOut 2.5s ease-in-out infinite",
            }}
          >
            {message.title}
          </p>
          <p
            style={{
              fontSize: 13,
              color: "#9CA3AF",
              lineHeight: "20px",
              fontWeight: 400,
            }}
          >
            {message.subtitle}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex gap-2 items-center">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: 7,
                height: 7,
                backgroundColor: "#1A47F0",
                animation: "hcDotBounce 1.4s ease-in-out infinite",
                animationDelay: `${i * 0.22}s`,
              }}
            />
          ))}
        </div>

        {/* Bottom label */}
        <p
          style={{
            fontSize: 12,
            color: "#D1D5DB",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          Secured by Tracelium
        </p>
      </div>

      <style>{`
        @keyframes hcSpin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes hcSpinReverse {
          from { transform: translate(-50%, -50%) rotate(360deg); }
          to   { transform: translate(-50%, -50%) rotate(0deg); }
        }
        @keyframes hcPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 0.5; }
          50%       { transform: translate(-50%, -50%) scale(1.08); opacity: 0.85; }
        }
        @keyframes hcIconPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50%       { transform: translate(-50%, -50%) scale(1.06); }
        }
        @keyframes hcGlow {
          0%, 100% { opacity: 0.35; }
          50%       { opacity: 0.75; }
        }
        @keyframes hcFadeInOut {
          0%, 100% { opacity: 0.75; }
          50%       { opacity: 1; }
        }
        @keyframes hcDotBounce {
          0%, 80%, 100% { transform: scale(0.75); opacity: 0.45; }
          40%            { transform: scale(1.25); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default HealthCheckLoadingOverlay
